import {
  Badge,
  Box,
  Dialog,
  FormControl,
  MenuItem,
  Select,
  styled,
  Typography,
  useTheme,
} from '@mui/material';

import { FareharborAvailability } from 'lib/features/checkout/checkoutService';
import { ReactComponent as CustomDropDownIcon } from '../../../assets/icons/black_down_arrow.svg';

import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { dateAndTimeFareharborStyles as styles } from "styles/Checkout/dateAndTimeFareharbor.styles";


// Styled Badge for marking dots
const CustomBadge = styled(Badge)(({ theme, invisible }) => ({
  "& .MuiBadge-dot": {
    bottom: "0px",
    left: "50%",
    transform: "translateX(-50%)",
    display: invisible ? "none" : "block",
  },
}));

interface DateAndTimeFareharborProps {
  itemType: number;
  selectedDate: dayjs.Dayjs | null;
  handleDateChange: (newDate: Dayjs | null) => void;
  isCalendarVisible: boolean;
  setIsCalendarVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTime: string;
  setSelectedTime: (time: string) => void;
  availableTimes: string[];
  fareharborAvailabilities?: FareharborAvailability[];
}

const DateAndTimeFareharbor: React.FC<DateAndTimeFareharborProps> = ({
  itemType,
  selectedDate,
  handleDateChange,
  isCalendarVisible,
  setIsCalendarVisible,
  selectedTime,
  setSelectedTime,
  availableTimes,
  fareharborAvailabilities,
}) => {
  const theme = useTheme();

  // Extract and deduplicate the marked dates
  const uniqueMarkedDates = Array.from(
    new Set(
      fareharborAvailabilities?.map((availability) =>
        dayjs(availability.start_at).format('YYYY-MM-DD')
      )
    )
  );

  // Custom Day component with a badge for marked dates
  const CustomDay = (props: any) => {
    const { day, outsideCurrentMonth, ...rest } = props;

    const formattedDate = dayjs(day).format("YYYY-MM-DD");
    const isToday = dayjs().isSame(day, 'day');

    if (outsideCurrentMonth) {
      return <PickersDay day={day} {...rest} outsideCurrentMonth={outsideCurrentMonth} />;
    }

    const isMarked = uniqueMarkedDates.includes(formattedDate);

    return (
      <CustomBadge
        overlap="circular"
        variant="dot"
        invisible={!isMarked}
        color="success"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <PickersDay day={day} outsideCurrentMonth={outsideCurrentMonth}  {...rest}
          sx={styles.customDay(isToday)}
        />
      </CustomBadge>
    );
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Typography
        sx={styles.checkAvailabilityText(theme)}
      >
        Check Availability
      </Typography>

      {/* Show Data */}
      <Box
        sx={styles.calendarBox}
      >
        {/* Date Picker */}
        <Box
          sx={styles.datePickerBox}
          onClick={() => setIsCalendarVisible(true)}
        >
          <Typography
            sx={styles.dateText(theme)}
          >
            {selectedDate?.format("ddd, MMM DD")}
          </Typography>
          <CustomDropDownIcon style={styles.dropdownIcon} />

        </Box>

        {/* Time Picker */}
        <Box
          sx={styles.timePickerBox}
        >
          <FormControl variant="outlined" sx={styles.selectBox}>
            <Select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              displayEmpty
              disabled={availableTimes?.length === 0}
              IconComponent={CustomDropDownIcon}
              renderValue={(selected) =>
                availableTimes.length > 0 ? selected || "Select Time" : "N/A"
              }
              sx={styles.selectInputTime}
            >
              {availableTimes?.length > 0 &&
                availableTimes.map((qty) => (
                  <MenuItem key={qty} value={qty.toString()}>
                    {qty}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Calendar Modal */}
      <Dialog
        open={isCalendarVisible}
        onClose={() => setIsCalendarVisible(false)}
        sx={styles.calendarDialog}
      >
        <DateCalendar
          value={selectedDate}
          onChange={handleDateChange}
          slots={{
            day: CustomDay,
          }}
          views={['day']}
          sx={styles.dateCalendar}
        />
      </Dialog>

    </LocalizationProvider>
  );
};

export default DateAndTimeFareharbor;
